'use client';

import { useEffect, useState, useMemo } from 'react';
import { createPublicClient, http, type PublicClient } from 'viem';
import { usePublicClient } from 'wagmi';
import { useChainStore } from '@/stores/chainStore';
import toast from 'react-hot-toast';
import { setSelectedRpc as setGlobalSelectedRpc } from '@/utils/wagmiRpcOverride';

// Global state để đảm bảo consistency
const globalSelectedRpcs = new Map<number, string>();
const globalPublicClients = new Map<number, PublicClient>();

// Hook để quản lý RPC và PublicClient
export function useSelectedRpc(chainId?: number) {
  const wagmiPublicClient = usePublicClient({ chainId });
  const { chains } = useChainStore();
  
  // Get RPC từ localStorage hoặc default
  const getRpcForChain = (chainId: number): string | null => {
    // Check global state first
    const cached = globalSelectedRpcs.get(chainId);
    if (cached) return cached;
    
    // Check localStorage
    const saved = localStorage.getItem(`helio-selected-rpc-${chainId}`);
    if (saved) {
      globalSelectedRpcs.set(chainId, saved);
      return saved;
    }
    
    return null;
  };
  
  // Get selected RPC for current chain
  const selectedRpc = useMemo(() => {
    if (!chainId) return null;
    const rpc = getRpcForChain(chainId);
    return rpc;
  }, [chainId, getRpcForChain]);

  // Create public client with selected RPC
  const publicClient = useMemo(() => {
    if (!selectedRpc || !chainId) {
      return null;
    }
    
    try {
      const client = createPublicClient({
        chain: {
          id: chainId,
          name: chains[chainId.toString()]?.name || 'Unknown Chain',
          network: chains[chainId.toString()]?.chain || chains[chainId.toString()]?.shortName,
          nativeCurrency: chains[chainId.toString()]?.nativeCurrency,
          rpcUrls: {
            default: { http: [selectedRpc] },
            public: { http: [selectedRpc] }
          },
          blockExplorers: chains[chainId.toString()]?.explorers?.[0] ? {
            default: {
              name: chains[chainId.toString()]?.explorers[0]?.name,
              url: chains[chainId.toString()]?.explorers[0]?.url
            }
          } : undefined
        },
        transport: http(selectedRpc, {
          batch: true,
          retryCount: 2,
          retryDelay: 1000,
        }),
      });
      
      return client;
    } catch (error) {
      console.error(`🌐 [useSelectedRpc] Failed to create public client:`, error);
      return null;
    }
  }, [chainId, selectedRpc, chains]);
  
  // Return custom client or wagmi default
  // const publicClient = customPublicClient || wagmiPublicClient; // This line is removed as per the new_code
  
  // Update when chain changes
  useEffect(() => {
    if (chainId) {
      const rpc = getRpcForChain(chainId);
      // setSelectedRpcState(rpc); // This line is removed as per the new_code
    }
  }, [chainId]);
  
  // Set RPC
  const setSelectedRpc = (rpc: string) => {
    if (!chainId) return;
    
    // Update all states
    globalSelectedRpcs.set(chainId, rpc);
    localStorage.setItem(`helio-selected-rpc-${chainId}`, rpc);
    // setSelectedRpcState(rpc); // This line is removed as per the new_code
    
    // Update global override
    setGlobalSelectedRpc(chainId, rpc);
    
    // Clear cached client to force recreation
    if (chainId) {
      globalPublicClients.delete(chainId);
    }
    
    // Show toast
    toast.success(`RPC switched to ${new URL(rpc).hostname}`);
    
    // Emit event for other components
    window.dispatchEvent(new CustomEvent('rpc-changed', {
      detail: { chainId, rpc }
    }));
  };
  
  // Listen for RPC changes from other components
  useEffect(() => {
    const handleRpcChange = (e: any) => {
      if (e.detail.chainId === chainId && chainId !== undefined) {
        // setSelectedRpcState(e.detail.rpc); // This line is removed as per the new_code
        // Clear cached client to force recreation
        globalPublicClients.delete(chainId);
      }
    };
    
    window.addEventListener('rpc-changed', handleRpcChange);
    return () => window.removeEventListener('rpc-changed', handleRpcChange);
  }, [chainId]);
  
  return {
    selectedRpc,
    setSelectedRpc,
    publicClient,
    chainId
  };
} 